import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContractAgency } from '@core/models/contract-agency.model';
import { ContractUt } from '@core/models/contract-ut.model';
import { ContractAgencyService } from '@core/services/contract-agency/contract-agency.service';
import { ContractUtService } from '@core/services/contract-ut/contract-ut.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-contract-manager-informations',
  templateUrl: './contract-manager-informations.component.html',
  styleUrls: ['./contract-manager-informations.component.scss'],
})
export class ContractManagerInformationsComponent implements OnInit {
  @Input() contractItemInformations: any;

  contractManagerFormGroup: FormGroup;

  // Autocomplete data
  isContractUtLoading = false;

  contractManagerList: ContractUt[] = [];
  filteredContractManagerList: Observable<ContractUt[]>;

  customerServiceManagerList: ContractUt[] = [];
  filteredCustomerServiceManagerList: Observable<ContractUt[]>;

  isCentralAgencyLoading = false;
  centralAgencyList: ContractAgency[] = [];
  filteredCentralAgencyList: Observable<ContractAgency[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    private contractUtService: ContractUtService,
    private contractAgencyService: ContractAgencyService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initContractManagerForm();
    this.getContractManagerList();
    this.getContractCustomerServiceManagerList();
    this.getContractAgencies();
  }

  public cancelFormsModification() {
    this.initForm();
    this.initContractManagerForm();
  }

  public isFormValid(): boolean {
    return this.contractManagerFormGroup.valid;
  }

  public getFormData() {
    return this.contractManagerFormGroup.value;
  }

  public markAllAsTouched() {
    this.contractManagerFormGroup.markAllAsTouched();
  }

  protected initForm() {
    this.contractManagerFormGroup = this.fb.group({
      contractManager: [null, Validators.required],
      customerServiceManager: [null, Validators.required],
      centralAgency: [null, Validators.required],
    });
  }

  private initContractManagerForm() {
    if (
      !this.contractItemInformations.contractManager ||
      !this.contractItemInformations.customerServiceManager ||
      !this.contractItemInformations.centralAgency
    ) {
      return;
    }

    let contractManager = new ContractUt(this.contractItemInformations.contractManager);
    let customerServiceManager = new ContractUt(this.contractItemInformations.customerServiceManager);
    let centralAgency = new ContractAgency(this.contractItemInformations.centralAgency);

    this.contractManagerFormGroup.patchValue({
      contractManager: contractManager,
      customerServiceManager: customerServiceManager,
      centralAgency: centralAgency,
    });
  }

  // --- Load autocomplete data ---

  // --- contract Manager ---

  private getContractManagerList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'contractManager' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;
          this.contractManagerList = contractUtList;

          this.initFilteredContractManagerList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredContractManagerList() {
    if (!this.isContractUtLoading) {
      this.filteredContractManagerList = this.contractManagerFormGroup.get('contractManager').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterContractManager(label) : this.contractManagerList.slice()))
      );
    }
  }

  private _filterContractManager(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();
    return this.contractManagerList.filter(
      contractManager =>
        contractManager.id.toString().toLowerCase().includes(filterValue) || contractManager.name.toLowerCase().includes(filterValue)
    );
  }

  // customer service manager

  private getContractCustomerServiceManagerList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'customerServiceManager' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;
          this.customerServiceManagerList = contractUtList;

          this.initFilteredCustomerServiceManagerList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredCustomerServiceManagerList() {
    if (!this.isContractUtLoading) {
      this.filteredCustomerServiceManagerList = this.contractManagerFormGroup.get('customerServiceManager').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterCustomerServiceManager(label) : this.customerServiceManagerList.slice()))
      );
    }
  }

  private _filterCustomerServiceManager(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();
    return this.customerServiceManagerList.filter(
      customerServiceManager =>
        customerServiceManager.id.toString().toLowerCase().includes(filterValue) ||
        customerServiceManager.name.toLowerCase().includes(filterValue)
    );
  }

  // --- contract agency ---

  private getContractAgencies() {
    this.isCentralAgencyLoading = true;

    this.contractAgencyService
      .getList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        centralAgencyList => {
          this.isCentralAgencyLoading = false;
          this.centralAgencyList = centralAgencyList;
          this.initFilteredContractAgencyList();
        },
        error => {
          this.isCentralAgencyLoading = false;
        }
      );
  }

  private initFilteredContractAgencyList() {
    if (!this.isCentralAgencyLoading) {
      this.filteredCentralAgencyList = this.contractManagerFormGroup.get('centralAgency').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterCentralAgency(label) : this.centralAgencyList.slice()))
      );
    }
  }

  private _filterCentralAgency(value: string = ''): ContractAgency[] {
    const filterValue = value.toLowerCase();
    return this.centralAgencyList.filter(
      centralAgency =>
        centralAgency.id.toString().toLowerCase().includes(filterValue) || centralAgency.name.toLowerCase().includes(filterValue)
    );
  }

  // --- Utility ---

  private _getLabel(value: string | ContractUt | ContractAgency): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public displayAutoCompleteData(data: any): string {
    if (!data) return '';
    return typeof data === 'string' ? data : data.name + ' (' + data.id + ')';
  }

  // --- On Destroy ---

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
